import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import PriceFormat from '@website/components/utilities/PriceFormat';
import { UserDashboardQuery } from '@website/types/graphqlOperations';
import { DateTime } from 'luxon';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Col, Row, Table } from 'react-bootstrap';

const creditHistoryColumns: ColumnDef<
  UserDashboardQuery['user']['credits'][number]['history']['items'][number]
>[] = [
  {
    header: 'Created On',
    accessorKey: 'created',
    cell: ({ getValue }) => {
      const date = DateTime.fromISO(getValue<string>()).toFormat('dd/MM/yyyy');
      return date;
    },
    size: 100,
  },
  {
    header: 'Amount',
    accessorKey: 'amountZD',
    cell: ({ getValue, row }) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { query } = useRouter();
      const userID = query.userID as string;
      const { bookingID, currency, note } = row.original;

      const amount = getValue<number>();

      return (
        <Row>
          <Col xs={12}>
            <PriceFormat amount={amount} currencyIso={currency} />{' '}
          </Col>
          <Col xs={12}>{note}</Col>
          <Col xs={12}>
            {bookingID && (
              <Col xs={12}>
                <Link href={`/me/${userID}/bookings/${bookingID}`}>
                  View Booking
                </Link>
              </Col>
            )}
          </Col>
        </Row>
      );
    },
    size: 300,
  },
];

type Props = {
  creditHistory: UserDashboardQuery['user']['credits'][number]['history']['items'];
};

export default function CreditHistoryTable({ creditHistory }: Props) {
  const table = useReactTable({
    columns: creditHistoryColumns,
    data: creditHistory,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Table striped bordered hover>
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map(
              ({ column, colSpan, id, getContext, getSize }) => (
                <th
                  key={id}
                  colSpan={colSpan}
                  className="p-2 px-2"
                  style={{
                    width: getSize(),
                  }}
                >
                  {flexRender(column.columnDef.header, getContext())}
                </th>
              ),
            )}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id}>
            {row.getAllCells().map((cell) => (
              <td key={cell.column.id} className="p-2 px-2">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
